// src/components/InstagramIntegration.tsx

import React, { useEffect, useState } from "react"
import { postRequest } from "utils/request"

declare global {
  interface Window {
    fbAsyncInit: () => void
    FB: any
  }
}

interface FacebookLoginResponse {
  authResponse: {
    accessToken: string
    userID: string
    expiresIn: number
    signedRequest: string
    reauthorize_required_in: number
    data_access_expiration_time: number
  }
  status: string
}

interface PageData {
  id: string
  name: string
  access_token: string
}

type PagesResponse = {
  error: null | undefined | any
  data: PageData[]
}

interface InstagramBusinessAccountResponse {
  instagram_business_account: {
    id: string
  }
  error?: null | undefined | any
}

const InstagramIntegration: React.FC = () => {
  const [sdkLoaded, setSdkLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = () => {
    if (!sdkLoaded) {
      setError("O SDK do Facebook ainda não foi carregado. Por favor, aguarde.")
      return
    }
    setLoading(true)
    window.FB.login(checkLoginState, {
      scope:
        "public_profile,instagram_basic,instagram_manage_messages,pages_show_list,pages_read_engagement,pages_manage_metadata",
      auth_type: "rerequest",
    })
  }

  const checkLoginState = (response: FacebookLoginResponse) => {
    if (response.authResponse) {
      // User granted permissions
      fetchUserData(response.authResponse)
    } else {
      setLoading(false)
      setError("Login cancelado ou não autorizado.")
    }
  }

  const fetchUserData = (
    authResponse: FacebookLoginResponse["authResponse"]
  ) => {
    const { accessToken, userID } = authResponse

    // Fetch user's pages
    window.FB.api("/me/accounts", "GET", {}, (response: PagesResponse) => {
      if (response && !response.error) {
        const pages = response.data
        // Optionally, let the user select the correct page if multiple pages are returned
        const page = pages[0] // For simplicity, using the first page

        if (page) {
          // Fetch Instagram business account ID
          window.FB.api(
            `/${page.id}`,
            "GET",
            { fields: "instagram_business_account" },
            (instaResponse: InstagramBusinessAccountResponse) => {
              if (instaResponse && !instaResponse.error) {
                const instagramBusinessAccountId =
                  instaResponse.instagram_business_account.id

                // Send data to your backend API
                sendDataToBackend({
                  accessToken: page.access_token, // Use the page access token
                  userID,
                  pageId: page.id,
                  instagramBusinessAccountId,
                })
              } else {
                setLoading(false)
                setError("Erro ao obter a conta comercial do Instagram.")
                console.error(
                  "Error fetching Instagram business account:",
                  instaResponse.error
                )
              }
            }
          )
        } else {
          setLoading(false)
          setError("Nenhuma página encontrada.")
          console.error("No pages found.")
        }
      } else {
        setLoading(false)
        setError("Erro ao obter as páginas do usuário.")
        console.error("Error fetching pages:", response.error)
      }
    })
  }

  const sendDataToBackend = (data: {
    accessToken: string
    userID: string
    pageId: string
    instagramBusinessAccountId: string
  }) => {
    const companyId = new URLSearchParams(window.location.search).get(
      "companyId"
    )

    postRequest(`/api/instagram-integration?companyId=${companyId}`, data)
      .then((response) => {
        setLoading(false)
        console.log("Data saved successfully:", response)
        // Optionally, redirect the user or show a success message
      })
      .catch((error) => {
        setLoading(false)
        setError("Erro ao salvar os dados.")
        console.error("Error saving data:", error)
      })
  }

  useEffect(() => {
    // Load the Facebook SDK script
    ;((d, s, id) => {
      const element = d.getElementsByTagName(s)[0]
      if (d.getElementById(id)) {
        return
      }
      const js = d.createElement(s) as HTMLScriptElement
      js.id = id
      js.src = "https://connect.facebook.net/en_US/sdk.js"
      element.parentNode?.insertBefore(js, element)
    })(document, "script", "facebook-jssdk")

    window.fbAsyncInit = () => {
      window.FB.init({
        appId: "YOUR_FACEBOOK_APP_ID",
        cookie: true,
        xfbml: true,
        version: "v18.0",
      })
      setSdkLoaded(true)
    }
  }, [])

  return (
    <div className="container">
      <h2>Integração com o Instagram</h2>
      <p>
        Para integrar sua conta do Instagram com o nosso CRM, por favor, leia os
        termos e autorize o acesso.
      </p>
      <h4>Termos e Condições</h4>
      <p>{/* Inclua aqui os termos e condições da sua empresa */}</p>
      <h4>Permissões Solicitadas</h4>
      <ul>
        <li>Acessar seu perfil público do Facebook</li>
        <li>Acessar informações básicas da sua conta do Instagram</li>
        <li>Gerenciar mensagens do Instagram</li>
        <li>Listar suas páginas do Facebook</li>
        <li>Ler o engajamento das suas páginas</li>
        <li>Gerenciar metadados das suas páginas</li>
      </ul>
      {error && <div className="alert alert-danger">{error}</div>}
      <button
        className="btn btn-primary"
        onClick={handleLogin}
        disabled={!sdkLoaded || loading}
      >
        {loading ? "Conectando..." : "Conectar com o Instagram"}
      </button>
      <div id="fb-root"></div>
    </div>
  )
}

export default InstagramIntegration
